import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, CheckCircle2, Circle, Clock, Flag, Calendar as CalIcon, User as UserIcon, ListTodo } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { TaskModal } from "@/components/TaskModal";

const STATUS_META = {
  open:        { label: "Offen",        icon: Circle,        color: "text-zinc-300" },
  in_progress: { label: "In Arbeit",    icon: Clock,         color: "text-amber-400" },
  done:        { label: "Erledigt",     icon: CheckCircle2,  color: "text-[#00E575]" },
  cancelled:   { label: "Abgebrochen",  icon: Circle,        color: "text-zinc-500" },
};

const PRIO_BADGE = {
  low:    "bg-zinc-700/40 text-zinc-300 border-zinc-700",
  normal: "bg-sky-600/20 text-sky-300 border-sky-700/40",
  high:   "bg-red-600/20 text-red-300 border-red-700/40",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const isOverdue = (t) => t.due_date && t.status === "open" && new Date(t.due_date) < new Date(new Date().toDateString());

export default function ProjectTasksSection({ project }) {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [members, setMembers] = useState([]);
  const [statusFilter, setStatusFilter] = useState("active"); // active | all | done
  const [modalOpen, setModalOpen] = useState(false);
  const [editTask, setEditTask] = useState(null);

  const isManager = user?.role === "admin" || user?.role === "sales_manager";

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/tasks", { params: { project_id: project.id } });
      setTasks(data);
    } catch { /* ignore */ }
  }, [project.id]);

  useEffect(() => {
    load();
    api.get("/calendar/config").then((r) => setMembers(r.data?.members || [])).catch(() => {});
  }, [load]);

  const filtered = useMemo(() => tasks.filter((t) => {
    if (statusFilter === "active") return t.status === "open" || t.status === "in_progress";
    if (statusFilter === "done") return t.status === "done";
    return true;
  }), [tasks, statusFilter]);

  const groups = useMemo(() => {
    const overdue = filtered.filter((t) => isOverdue(t));
    const today = filtered.filter((t) => !isOverdue(t) && t.due_date && new Date(t.due_date).toDateString() === new Date().toDateString());
    const upcoming = filtered.filter((t) => !isOverdue(t) && t.due_date && new Date(t.due_date).toDateString() !== new Date().toDateString() && new Date(t.due_date) > new Date());
    const noDate = filtered.filter((t) => !t.due_date);
    return { overdue, today, upcoming, noDate };
  }, [filtered]);

  const memberName = (id) => {
    if (!id) return "";
    if (id === user?.id) return "Ich";
    const m = members.find((mm) => mm.id === id);
    return m ? m.name : "—";
  };

  const toggleStatus = async (t) => {
    const next = t.status === "done" ? "open" : "done";
    try {
      await api.patch(`/tasks/${t.id}`, { status: next });
      toast.success(next === "done" ? "Erledigt" : "Wieder offen");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  const openNew = () => { setEditTask(null); setModalOpen(true); };
  const openEdit = (t) => { setEditTask(t); setModalOpen(true); };

  const Section = ({ title, items, accent }) => {
    if (items.length === 0) return null;
    return (
      <div className="mb-5">
        <h3 className={`text-xs uppercase font-mono tracking-wider mb-2 ${accent || "text-zinc-400"}`}>
          {title} <span className="text-zinc-600">({items.length})</span>
        </h3>
        <div className="space-y-2">
          {items.map((t) => {
            const Sm = STATUS_META[t.status] || STATUS_META.open;
            const StIcon = Sm.icon;
            const overdue = isOverdue(t);
            return (
              <div key={t.id} data-testid={`project-task-row-${t.id}`} className={`flex items-start gap-3 bg-[#18181B] border ${overdue ? "border-red-600/40" : "border-zinc-800"} rounded-xl p-3 hover:border-zinc-700 transition`}>
                <button
                  onClick={() => toggleStatus(t)}
                  data-testid={`project-task-toggle-${t.id}`}
                  className={`mt-0.5 ${Sm.color} hover:opacity-80`}
                  aria-label="Status umschalten"
                >
                  <StIcon size={20} />
                </button>
                <button onClick={() => openEdit(t)} className="flex-1 min-w-0 text-left">
                  <div className={`font-medium ${t.status === "done" ? "line-through text-zinc-500" : ""}`}>{t.title}</div>
                  <div className="flex flex-wrap items-center gap-2 mt-1 text-[12px] text-zinc-400">
                    {t.due_date && (
                      <span className={`inline-flex items-center gap-1 ${overdue ? "text-red-400 font-semibold" : ""}`}>
                        <CalIcon size={11} /> {fmtDate(t.due_date)}
                      </span>
                    )}
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border ${PRIO_BADGE[t.priority] || PRIO_BADGE.normal}`}>
                      <Flag size={10} /> {t.priority}
                    </span>
                    {isManager && t.owner_user_id && (
                      <span className="inline-flex items-center gap-1">
                        <UserIcon size={11} /> {memberName(t.owner_user_id)}
                      </span>
                    )}
                  </div>
                  {t.description && <div className="mt-1 text-xs text-zinc-300 line-clamp-2">{t.description}</div>}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="project-tasks-section">
      <div className="bg-[#00E575]/10 border border-[#00E575]/30 rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-1">
          <ListTodo size={16} className="text-[#00E575]" />
          <span className="font-semibold text-[#00E575] text-[13px]">Aufgaben zu diesem Projekt</span>
        </div>
        <p className="text-[12.5px] text-zinc-300 leading-relaxed">
          Alle Aufgaben, die du oder dein Team für <span className="font-semibold">{project.project_number}</span> angelegt habt – Fälligkeiten, Status &amp; Verantwortliche auf einen Blick.
        </p>
      </div>

      <div className="flex items-center justify-between gap-3">
        <div className="flex gap-1 rounded-xl bg-[#18181B] border border-zinc-800 overflow-hidden">
          <button onClick={() => setStatusFilter("active")} data-testid="project-tasks-filter-active" className={`px-3 h-10 text-sm ${statusFilter==="active"?"bg-[#00E575] text-[#09090B] font-semibold":"text-zinc-300 hover:bg-zinc-800"}`}>Aktiv</button>
          <button onClick={() => setStatusFilter("done")} data-testid="project-tasks-filter-done" className={`px-3 h-10 text-sm ${statusFilter==="done"?"bg-[#00E575] text-[#09090B] font-semibold":"text-zinc-300 hover:bg-zinc-800"}`}>Erledigt</button>
          <button onClick={() => setStatusFilter("all")} data-testid="project-tasks-filter-all" className={`px-3 h-10 text-sm ${statusFilter==="all"?"bg-[#00E575] text-[#09090B] font-semibold":"text-zinc-300 hover:bg-zinc-800"}`}>Alle</button>
        </div>
        <button
          onClick={openNew}
          data-testid="project-tasks-new-btn"
          className="h-10 inline-flex items-center gap-2 px-3 rounded-xl bg-[#00E575] text-[#09090B] font-semibold text-sm hover:bg-[#00C964] active:scale-95 transition"
        >
          <Plus size={16} /> Aufgabe
        </button>
      </div>

      <Section title="Überfällig" items={groups.overdue} accent="text-red-400" />
      <Section title="Heute" items={groups.today} accent="text-amber-400" />
      <Section title="Anstehend" items={groups.upcoming} />
      <Section title="Ohne Termin" items={groups.noDate} />

      {filtered.length === 0 && (
        <div className="bg-[#18181B] border border-dashed border-zinc-700 rounded-2xl p-8 text-center text-zinc-500">
          Keine Aufgaben für dieses Projekt. Lege oben rechts deine erste Aufgabe an.
        </div>
      )}

      <TaskModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        task={editTask}
        members={members}
        defaultProjectId={project.id}
        onSaved={load}
      />
    </div>
  );
}
